import { Breakpoint, Text } from "@relode/core";
import { JobCandidateStatus, JobStatus } from "@relode/core/domain";
import * as React from "react";

import {
    DashboardContent,
    DashboardTile,
    DashboardTileSection,
} from "../../../shared/components";
import { useDashboardContext } from "../_infra/dashboardProvider";
import { goToCandidateSearchStatus, goToJobSearchStatus } from "../goToSearch";
import { QuestionsSection } from "../questionsSection";
import { UpcomingInterviews } from "./upcomingInterviews";

const gotoSubmittedCandidates = () => {
    goToCandidateSearchStatus({
        jobCandidateStatus: JobCandidateStatus.Submitted,
        jobStatus: [JobStatus.Open, JobStatus.Paused],
    });
};

const gotoInterviewingPastCandidates = () => {
    goToCandidateSearchStatus({
        jobCandidateStatus: JobCandidateStatus.Interviewing,
        isInterviewingUpcoming: false,
        jobStatus: [JobStatus.Open, JobStatus.Paused],
    });
};

export const HiringTeamMemberDashboard = () => {
    const {
        state: {
            data: {
                memberStats,
                jobManagerDetails,
                upcomingMemberInterviews,
                memberJobCounts,
            },
            ui: { memberIsBusy },
        },
    } = useDashboardContext();

    const displayCount = (count: number | undefined | null) => {
        if (count === undefined) return "";
        if (count === null) return <Text variant="h2">n/a</Text>;
        return <Text variant="h2">{count}</Text>;
    };

    const displayInterviewingPastSubTitle = (awaitingFeedback: number) => {
        if (!!awaitingFeedback)
            return (
                <>
                    <Text variant="h5" isInline={true}>
                        {awaitingFeedback}
                    </Text>{" "}
                    awaiting feedback
                </>
            );

        return null;
    };

    const goToOpenJobs = () => {
        goToJobSearchStatus(JobStatus.Open);
    };

    const goToPausedJobs = () => {
        goToJobSearchStatus(JobStatus.Paused);
    };

    const goToClosedJobs = () => {
        goToJobSearchStatus(JobStatus.Closed);
    };

    return (
        <DashboardContent>
            <DashboardTileSection
                usePaper
                title={<Text variant="h2">my candidates</Text>}
            >
                <DashboardTile
                    id="submittedTile"
                    title="submitted"
                    isBusy={memberIsBusy.submitted}
                    onClick={gotoSubmittedCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(memberStats.submitted)}
                </DashboardTile>
                <DashboardTile
                    id="interviewingAwaitingFeedbackTile"
                    title="interviewed"
                    subTitle={displayInterviewingPastSubTitle(
                        memberStats.interviewingPastAwaitingFeedback
                    )}
                    isBusy={
                        memberIsBusy.interviewingPast ||
                        memberIsBusy.interviewingPastAwaitingFeedback
                    }
                    onClick={gotoInterviewingPastCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(memberStats.interviewingPast)}
                </DashboardTile>
            </DashboardTileSection>
            {(upcomingMemberInterviews?.length ?? 0) > 0 ? (
                <DashboardTileSection usePaper title="upcoming interviews">
                    <UpcomingInterviews data={upcomingMemberInterviews} />
                </DashboardTileSection>
            ) : null}
            <DashboardTileSection usePaper title="my jobs">
                <DashboardTile
                    title="open"
                    id="openJobs"
                    isBusy={memberIsBusy.jobCounts}
                    onClick={goToOpenJobs}
                    showAsLink={true}
                    small
                >
                    {displayCount(memberJobCounts.open)}
                </DashboardTile>
                <DashboardTile
                    title="paused"
                    id="pausedJobs"
                    isBusy={memberIsBusy.jobCounts}
                    onClick={goToPausedJobs}
                    showAsLink={true}
                    small
                >
                    {displayCount(memberJobCounts.paused)}
                </DashboardTile>
                <DashboardTile
                    title="closed"
                    id="closedJobs"
                    isBusy={memberIsBusy.jobCounts}
                    onClick={goToClosedJobs}
                    showAsLink={true}
                    small
                >
                    {displayCount(memberJobCounts.closed)}
                </DashboardTile>
                <Breakpoint mobile>
                    <DashboardTile id="emptyJobs" small>
                        <></>
                    </DashboardTile>
                </Breakpoint>
            </DashboardTileSection>
            {jobManagerDetails ? (
                <DashboardTileSection usePaper title="questions?" fitContent>
                    <QuestionsSection
                        id="dashboardQuestions"
                        jobManagerProfile={jobManagerDetails}
                    />
                </DashboardTileSection>
            ) : null}
        </DashboardContent>
    );
};
